/**
 * Live SSE tail of the current server log session.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { SessionLogEntry } from "@memes/shared";
import { devLogsApi } from "../../lib/api";
import {
  filterLogEntries,
  findLogEntryIndex,
  isErrorLevel,
  sliceLogContext,
  type PinnedLogContext,
} from "../../lib/logFormat";
import { LogEntryDetail } from "../../components/dev/LogEntryDetail";
import { LogFilterBar } from "../../components/dev/LogFilterBar";
import { LogLine } from "../../components/dev/LogLine";

const MAX_LINES = 1500;

function formatPinTime(ts: string): string {
  try {
    return new Date(ts).toLocaleTimeString(undefined, {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      fractionalSecondDigits: 3,
    });
  } catch {
    return ts;
  }
}

export const DevLogLive: React.FC = () => {
  const [entries, setEntries] = useState<SessionLogEntry[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [filter, setFilter] = useState("");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [selected, setSelected] = useState<SessionLogEntry | null>(null);
  const [pinned, setPinned] = useState<PinnedLogContext | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pausedByScroll = useRef(false);

  useEffect(() => {
    const es = new EventSource(devLogsApi.liveStreamUrl(), { withCredentials: true });

    es.addEventListener("meta", (ev) => {
      try {
        const data = JSON.parse((ev as MessageEvent).data) as { sessionId?: string };
        if (data.sessionId) setSessionId(data.sessionId);
      } catch {
        /* ignore */
      }
    });

    es.addEventListener("log", (ev) => {
      try {
        const entry = JSON.parse((ev as MessageEvent).data) as SessionLogEntry;
        setEntries((prev) => {
          const next = [...prev, entry];
          return next.length > MAX_LINES ? next.slice(-MAX_LINES) : next;
        });
      } catch {
        /* ignore */
      }
    });

    es.onopen = () => setConnected(true);
    es.onerror = () => setConnected(false);

    return () => {
      es.close();
      setConnected(false);
    };
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || pinned || !autoScroll || pausedByScroll.current) return;
    el.scrollTop = el.scrollHeight;
  }, [entries, autoScroll, pinned]);

  useEffect(() => {
    if (!pinned || !selected) return;
    document.getElementById("live-log-line-selected")?.scrollIntoView({ block: "center" });
  }, [pinned, selected]);

  const onScroll = (): void => {
    if (pinned) return;
    const el = containerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    pausedByScroll.current = !atBottom;
    if (atBottom) setAutoScroll(true);
  };

  const pinEntry = (entry: SessionLogEntry): void => {
    const index = findLogEntryIndex(entries, entry);
    if (index < 0) return;
    setPinned(sliceLogContext(entries, index));
    setSelected(entry);
    setAutoScroll(false);
    pausedByScroll.current = true;
  };

  const resumeLive = (): void => {
    setPinned(null);
    setSelected(null);
    setAutoScroll(true);
    pausedByScroll.current = false;
  };

  const listSource = pinned ? pinned.lines : entries;
  const visible = filterLogEntries(listSource, { q: filter, errorsOnly });
  const hiddenSincePin = pinned ? Math.max(0, entries.length - pinned.bufferedAtPin) : 0;

  const errCount = entries.filter((e) => isErrorLevel(e.level)).length;
  const warnCount = entries.filter((e) => e.level === "warn").length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Link to="/dev/logs" className="text-indigo-600 hover:underline dark:text-indigo-400">
            ← Sessions
          </Link>
          {sessionId ? (
            <span className="font-mono text-xs text-gray-500">{sessionId.slice(0, 8)}…</span>
          ) : null}
          <span
            className={`inline-flex items-center gap-1 text-xs ${connected ? "text-green-600" : "text-red-600"}`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${connected ? "bg-green-500" : "bg-red-500"}`}
            />
            {connected ? "Connected" : "Disconnected"}
          </span>
          <span className="text-gray-500">
            {errCount} errors · {warnCount} warnings · {entries.length} lines buffered
          </span>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={autoScroll && !pinned}
            disabled={!!pinned}
            onChange={(e) => {
              setAutoScroll(e.target.checked);
              if (e.target.checked) pausedByScroll.current = false;
            }}
          />
          Auto-scroll
        </label>
      </div>

      {pinned && selected ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm dark:border-amber-900 dark:bg-amber-950/40">
          <div className="min-w-0">
            <span className="font-medium text-amber-900 dark:text-amber-100">
              Frozen context around {formatPinTime(selected.ts)}
            </span>
            <span className="ml-2 text-xs text-amber-800/80 dark:text-amber-200/80">
              {pinned.lines.length} lines · live tail paused
              {hiddenSincePin > 0
                ? ` · ${hiddenSincePin} new line${hiddenSincePin === 1 ? "" : "s"} buffered`
                : ""}
            </span>
          </div>
          <button type="button" className="btn text-xs" onClick={resumeLive}>
            Resume live tail
          </button>
        </div>
      ) : null}

      <div className="card p-3">
        <LogFilterBar
          search={filter}
          onSearchChange={setFilter}
          searchPlaceholder="Filter live stream…"
          errorsOnly={errorsOnly}
          onErrorsOnlyChange={setErrorsOnly}
        />
      </div>

      <div className="card overflow-hidden bg-gray-950/5 dark:bg-black/30">
        <div
          ref={containerRef}
          onScroll={onScroll}
          className="max-h-[55vh] overflow-y-auto"
        >
          {visible.map((entry, i) => {
            const isSelected =
              selected?.ts === entry.ts &&
              selected?.msg === entry.msg &&
              selected?.source === entry.source;
            return (
              <LogLine
                key={`${entry.ts}-${entry.source}-${entry.msg}-${i}`}
                id={isSelected ? "live-log-line-selected" : undefined}
                entry={entry}
                selected={isSelected}
                onSelect={pinEntry}
              />
            );
          })}
          {!visible.length ? (
            <p className="p-4 text-sm text-gray-500">
              {listSource.length ? "No lines match filter." : "Waiting for log events…"}
            </p>
          ) : null}
        </div>
        {selected ? (
          <LogEntryDetail
            entry={selected}
            onClose={() => {
              if (pinned) resumeLive();
              else setSelected(null);
            }}
          />
        ) : (
          <p className="border-t border-gray-100 px-3 py-2 text-xs text-gray-400 dark:border-gray-800">
            Click any line to freeze context around it and inspect details
          </p>
        )}
      </div>
    </div>
  );
};
